import { useCallback, useEffect, useRef, useState } from "react";
import { TURNSTILE_SITE_KEY, isTurnstileEnabled } from "@/config/turnstile";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
      execute: (id?: string, opts?: Record<string, unknown>) => void;
    };
  }
}

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile_script_error")));
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile_script_error"));
    document.head.appendChild(script);
  });
}

/**
 * CAPTCHA invisible Cloudflare Turnstile.
 * Aucune interaction pour les visiteuses légitimes : le token est généré en fond.
 */
export const useTurnstile = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const resolversRef = useRef<((token: string | null) => void)[]>([]);
  const [ready, setReady] = useState(false);

  const settle = useCallback((token: string | null) => {
    tokenRef.current = token;
    const pending = resolversRef.current;
    resolversRef.current = [];
    pending.forEach((resolve) => resolve(token));
  }, []);

  useEffect(() => {
    if (!isTurnstileEnabled()) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          size: "invisible",
          appearance: "interaction-only",
          callback: (token: string) => settle(token),
          "error-callback": () => settle(null),
          "expired-callback": () => {
            tokenRef.current = null;
          },
        });
        setReady(true);
      })
      .catch(() => setReady(false));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
      }
      widgetIdRef.current = null;
    };
  }, [settle]);

  /** Renvoie un token frais, ou null si Turnstile est désactivé / indisponible. */
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!isTurnstileEnabled() || !ready || !window.turnstile || !widgetIdRef.current) return null;
    if (tokenRef.current) return tokenRef.current;

    return new Promise<string | null>((resolve) => {
      resolversRef.current.push(resolve);
      try {
        window.turnstile!.execute(widgetIdRef.current!, {});
      } catch {
        settle(null);
      }
      // Filet de sécurité : ne jamais bloquer la visiteuse
      setTimeout(() => {
        if (resolversRef.current.includes(resolve)) {
          resolversRef.current = resolversRef.current.filter((r) => r !== resolve);
          resolve(null);
        }
      }, 8000);
    });
  }, [ready, settle]);

  /** Réinitialise le widget après une soumission réussie. */
  const reset = useCallback(() => {
    tokenRef.current = null;
    if (window.turnstile && widgetIdRef.current) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        /* ignore */
      }
    }
  }, []);

  return { containerRef, getToken, reset, enabled: isTurnstileEnabled() };
};

export default useTurnstile;
